import React, { useState, useCallback, useMemo } from 'react';
import {
  Button,
  Autocomplete,
  TextField,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';

const SELECTION_MODE = {
  ROLES: 'ROLES',
  SITES: 'SITES',
};

const composeSearchParams = data => {
  const encodedParams = Object.entries(data).map(([key, val]) => {
    return `${key}=${encodeURIComponent(val)}`;
  });

  return `?${encodedParams.join('&')}`;
};

const UserStudyItem = ({
  appBasePath = 'app',
  sponsorName,
  studyName,
  studyDescription,
  studyId,
  tenantId,
  setSelectionMode,
  selectionMode,
  roles,
}) => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const [isUnblindedRoleRealized, setIsUnblindedRoleRealized] = useState(false);

  const openStudy = useCallback(params => {
    const search = composeSearchParams({
      ...params,
      studyId,
      tenantId,
      basePath: appBasePath,
    });

    window.location.href = `/${appBasePath}/${search}`;
  }, [studyId, tenantId, appBasePath]);

  const selectStudy = useCallback(() => {
    if (!!selectionMode) return;

    if (!Array.isArray(roles)) {
      openStudy({
        roleId: undefined,
        siteId: undefined,
      });
    } else if (roles.length === 1) {
      if (roles[0].roleSiteSelectionEnabled) {
        setSelectedRole({
          sites: roles[0].sites,
          id: roles[0].roleId,
          label: roles[0].roleName,
        });

        setSelectionMode({
          studyId,
          mode: SELECTION_MODE.SITES,
        });
      } else if (roles[0].sites.length > 1) {
        openStudy({
          roleId: roles[0].roleId,
          siteId: undefined,
        });
      } else {
        openStudy({
          roleId: roles[0].roleId,
          siteId: roles[0].sites[0]?.siteId,
        });
      }
    } else {
      setSelectionMode({
        studyId,
        mode: SELECTION_MODE.ROLES,
      });
    }
  }, [selectionMode, studyId, roles, setSelectionMode, openStudy]);

  const onCancel = useCallback(() => {
    setSelectionMode(null);
    setSelectedRole(null);
    setIsUnblindedRoleRealized(false);
  }, [setSelectionMode]);

  const onConfirm = useCallback(() => {
    if (!selectedSite) {
      if (selectedRole.roleSiteSelectionEnabled) {
        setSelectionMode({
          studyId,
          mode: SELECTION_MODE.SITES,
        });
      } else if (selectedRole.sites.length > 1) {
        openStudy({
          roleId: selectedRole.id,
          siteId: undefined,
        });
      } else {
        openStudy({
          roleId: selectedRole.id,
          siteId: selectedRole.sites[0]?.siteId,
        });
      }
    } else {
      openStudy({
        roleId: selectedRole.id,
        siteId: selectedSite.id,
      });
    }
  }, [selectedRole, selectedSite, studyId, setSelectionMode, openStudy]);

  const onRoleChange = useCallback((_, roleSelection) => {
    setIsUnblindedRoleRealized(false);
    setSelectedRole(roleSelection);
  }, []);

  const onWarningConfirmationChange = useCallback((_, isConfirmed) => {
    setIsUnblindedRoleRealized(isConfirmed);
  }, []);

  const onSiteChange = useCallback((_, siteSelection) => {
    setSelectedSite(siteSelection);
  }, []);

  const roleOptions = useMemo(() => {
    if (!Array.isArray(roles)) return [];

    return roles.map(({ roleId, roleName, sites }) => ({
      sites,
      id: roleId,
      label: roleName,
    }));
  }, [roles]);

  const siteOptions = useMemo(() => {
    const composeOpt = ({ siteId, siteName }) => ({ id: siteId, label: siteName });

    if (!!selectedRole) {
      return selectedRole.sites.map(composeOpt);
    }

    if (!Array.isArray(roles)) return [];

    return roles[0].sites.map(composeOpt);
  }, [roles, selectedRole]);

  const isRoleSelectionUnblinded = useMemo(() => {
    if (!Array.isArray(roles) || !selectedRole) return false;

    return roles.find(({ roleId }) => roleId === selectedRole.id).isUnblinded;
  }, [roles, selectedRole]);

  const isConfirmDisabled = useMemo(() => {
    switch (selectionMode?.mode) {
    case SELECTION_MODE.ROLES:
      if (isRoleSelectionUnblinded && !isUnblindedRoleRealized) {
        return true;
      }

      return !selectedRole;
    case SELECTION_MODE.SITES:
      return !selectedSite;
    default:
      return false;
    }
  }, [selectionMode, selectedRole, selectedSite, isUnblindedRoleRealized, isRoleSelectionUnblinded]);

  const renderInput = useCallback((label, params) => (
    <TextField {...params} label={label} />
  ), []);

  const checkboxControl = useMemo(() => {
    return (
      <Checkbox
        checked={isUnblindedRoleRealized}
        onChange={onWarningConfirmationChange}
      />
    );
  }, [isUnblindedRoleRealized, onWarningConfirmationChange]);

  const roleSelectContent = useMemo(() => {
    if (selectionMode?.mode !== SELECTION_MODE.ROLES) return null;

    return (
      <section>
        <div className="content-container">
          <h4>Please select the role you wish to log in with.</h4>

          <Autocomplete
            disablePortal
            size="small"
            options={roleOptions}
            renderInput={renderInput.bind(this, 'Select Role')}
            onChange={onRoleChange}
          />
        </div>

        {isRoleSelectionUnblinded ? (
          <div className="content-container warning">
            <div>
              <WarningAmberIcon />
              <span>{'You\'ve selected an UNBLINDED user role.'}</span>
            </div>

            <div>
              <FormControlLabel
                control={checkboxControl}
                label="I realize I'm logging in with an UNBLINDED user role."
              />
            </div>
          </div>
        ) : null}
      </section>
    );
  }, [selectionMode, roleOptions, isRoleSelectionUnblinded, renderInput, checkboxControl, onRoleChange]);

  const siteSelectContent = useMemo(() => {
    if (selectionMode?.mode !== SELECTION_MODE.SITES) return null;

    return (
      <section>
        <div className="content-container">
          <h4>Please select the site you wish to log into.</h4>

          <Autocomplete
            disablePortal
            size="small"
            options={siteOptions}
            renderInput={renderInput.bind(this, 'Select Site')}
            onChange={onSiteChange}
          />
        </div>
      </section>
    );
  }, [selectionMode, siteOptions, renderInput, onSiteChange]);

  return (
    <li className="user-study-item" onClick={selectStudy}>
      <header>
        <h4>{sponsorName}</h4>
      </header>

      <section>
        <div className="study-details">
          <h3>{studyName}</h3>
          <p>{studyDescription}</p>
        </div>

        {!selectionMode ? (
          <div>
            <Button
              variant="contained"
              color="secondary"
              endIcon={(<ArrowForwardIcon />)}
              onClick={selectStudy}>
              Select Study
            </Button>
          </div>
        ) : null}
      </section>

      {roleSelectContent}

      {siteSelectContent}

      {!!selectionMode ? (
        <footer>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={(<ArrowBackIcon />)}
            onClick={onCancel}>
            Go Back
          </Button>

          <Button
            variant="contained"
            color="secondary"
            startIcon={(<CheckIcon />)}
            onClick={onConfirm}
            disabled={isConfirmDisabled}>
            Confirm
          </Button>
        </footer>
      ) : null}
    </li>
  );
};

export default UserStudyItem;

